import React from 'react';
import { Button, Skeleton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import {
  CallScribeAccessErrorSelector,
  CallScribeAccessLoadingSelector,
  CallScribeAccessSelector,
} from 'store/callScribe/selectors';
import { ThunkGetCallScribeAccess } from 'store/callScribe/thunks';
import useStyles from 'components/CallScribe/styles';

export interface ICallScribeAccessGateProps {
  children: React.ReactNode;
}

const CallScribeAccessGate: React.FC<ICallScribeAccessGateProps> = ({ children }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const access = useAppSelector(CallScribeAccessSelector);
  const accessError = useAppSelector(CallScribeAccessErrorSelector);
  const accessLoading = useAppSelector(CallScribeAccessLoadingSelector);

  if (accessLoading || (!access && !accessError)) {
    return (
      <div className={classes.container}>
        <Skeleton variant="rounded" height={56} />
        <Skeleton variant="rounded" height={38} width={180} />
        <Skeleton variant="rounded" height={220} />
      </div>
    );
  }

  if (accessError) {
    return (
      <div className={classes.container}>
        <div className={classes.emptyState} role="alert">
          {accessError}
          <div className={classes.meta}>
            This is usually a temporary network problem, not a change to your plan.
          </div>
          <Button
            size="small"
            variant="outlined"
            className={classes.retryButton}
            startIcon={<RefreshIcon fontSize="small" />}
            onClick={() => { void dispatch(ThunkGetCallScribeAccess()); }}
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!access?.hasAccess) {
    return (
      <div className={classes.container}>
        <div className={classes.emptyState}>
          CallScribe is not enabled for this tenant.
          {access?.reason ? <div className={classes.meta}>{access.reason}</div> : null}
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default CallScribeAccessGate;
